import { RequestStatus } from 'typed/api';
import { Entity, NetworkArray } from 'typed/api/entities';
import { IAction } from './action';
import networkAction, { NetworkAction } from './networkAction';
import { EntityActionMap, IEntityActionCreatorSet, InferredActionType, PartialEntity, ProvidedKeys } from './entityActionSet';

export interface TripleNestedEntityActionCreatorSet<T extends string, GG extends Entity, G extends Entity, P extends Entity, E extends Entity> extends IEntityActionCreatorSet<T, E> {
    loadAll(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], ...options: unknown[]): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; }, NetworkArray<E>>;
    load(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entityID: E['ID'], ...options: unknown[]): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entityID: E['ID']; }, E>;
    addAll(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[]): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entities: PartialEntity<E>[]; }, void>;
    addAll(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync: true): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entities: PartialEntity<E>[]; }, number[]>;
    add(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entity: PartialEntity<E>; }, void>;
    add(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync: true): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entity: PartialEntity<E>; }, number>;
    editAll(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync?: boolean, reloadAfterSync?: boolean): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entities: PartialEntity<E>[]; }, void>;
    edit(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync?: boolean): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entity: PartialEntity<E>; }, void>;
    deleteAll(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: E[]): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entityIDs: E['ID'][]; }, void>;
    delete(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: E, sync?: boolean): NetworkAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; entityID: E['ID']; }, void>;
    voidAll(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], immediately?: true): IAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; immediately: boolean; }>;
    void(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entityID: E['ID'], immediately?: true): IAction<T, { grandGrandParentID: GG['ID']; grandParentID: G['ID']; parentID: P['ID']; immediately: boolean; entityID: E['ID']; }>;
}

export type TripleNestedEntityMappedActionCreators<GG extends Entity, G extends Entity, P extends Entity, E extends Entity, M extends EntityActionMap<any, E>> =
    { readonly [K in ProvidedKeys<M>]: TripleNestedEntityActionCreatorSet<InferredActionType<M, K>, GG, G, P, E>[K] };

/**
 * @param grandGrandParent a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
 * @param grandParent a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
 * @param parent a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
 * @param entity a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
  */
export default function tripleNestedEntityNetworkActionSet<GG extends Entity, G extends Entity, P extends Entity, E extends Entity, M extends EntityActionMap<any, E>>(_grandGrandParent: GG, _grandParent: G, _parent: P, _entity: E, map: M): { readonly [K in ProvidedKeys<M>]: TripleNestedEntityActionCreatorSet<InferredActionType<M, K>, GG, G, P, E>[K] } {
    // The return type is actually MappedActionCreators<E, M>, but if result is declared this way, type hints/intellisense just show the name, which is not so helpful.
    // Explicit declaration here causes nice hints of all available methods, overloads etc.

    const
        addAllType = map.addAll && map.addAll.type!,
        addAllMethod = map.addAll && map.addAll.method!,
        addAllAction: (<T extends string, P>(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: number, entities: PartialEntity<E>[]) => NetworkAction<T, P, number[]>) | null = addAllType && addAllMethod ?
            (grandGrandParentID, grandParentID, parentID, entities) => networkAction(
                addAllType, { grandGrandParentID, grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities }, addAllMethod, entities
            ) :
            null,

        addType = map.add && map.add.type!,
        addMethod = map.add && map.add.method!,
        addAction: (<T extends string, P>(grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: number, entity: Partial<E>) => NetworkAction<T, P, number>) | null = addType && addMethod ?
            (grandGrandParentID, grandParentID, parentID, entity) => networkAction(
                addType, { grandGrandParentID, grandParentID, parentID, entityID: entity.ID, entity }, addMethod, entity
            ) :
            null,


        result = {} as unknown as TripleNestedEntityActionCreatorSet<string, GG, G, P, E>;

    if (map.loadAll) {
        const
            type = map.loadAll.type!,
            method = map.loadAll.method!;

        result.loadAll = (grandGrandParentID, grandParentID, parentID, ...options) => networkAction(type, { grandGrandParentID, grandParentID, parentID }, method, ...options);
    }

    if (map.load) {
        const
            type = map.load.type!,
            method = map.load.method!;

        result.load = (grandGrandParentID, grandParentID, parentID, entityID, ...options) => networkAction(
            type, { grandGrandParentID, grandParentID, parentID, entityID }, method, entityID, ...options
        );
    }

    if (map.addAll) {
        if (!addAllType || !addAllAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).addAll = (grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync = false, reloadAllAfterSync = false, ...reloadAllOptions: unknown[]): NetworkAction<typeof addAllType, any, any> =>
            async(dispatch) => {
                dispatch({
                    type: addAllType,
                    payload: { grandGrandParentID, grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entities.map(({ ID }) => ID);
                }
                else if (!reloadAllAfterSync || !DEBUG && !result.loadAll) {
                    return await dispatch(addAllAction(grandGrandParentID, grandParentID, parentID, entities)) as any;
                }
                else {
                    if (result.loadAll) {
                        await dispatch(addAllAction(grandGrandParentID, grandParentID, parentID, entities));
                        return await dispatch(result.loadAll(grandGrandParentID, grandParentID, parentID, ...reloadAllOptions));
                    }

                    throw new Error('Can\'t reloadAll after sync - methodsMap.loadAll is not configured.');
                }
            };
    }


    if (map.editAll) {
        const
            type = map.editAll.type!,
            method = map.editAll.method!,
            editAllAction = (grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[]) =>
                networkAction(type, { grandGrandParentID, grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities }, method, entities);

        (result as any).editAll = (grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync = false, reloadAllAfterSync = false, ...reloadAllOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { grandGrandParentID, grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAllAfterSync || !DEBUG && !result.loadAll) {
                    return await dispatch(editAllAction(grandGrandParentID, grandParentID, parentID, entities));
                }
                else {
                    if (result.loadAll) {
                        await dispatch(editAllAction(grandGrandParentID, grandParentID, parentID, entities));
                        return await dispatch(result.loadAll(grandGrandParentID, grandParentID, parentID, ...reloadAllOptions));
                    }


                    throw new Error('Can\'t reloadAll after sync - methodsMap.loadAll is not configured.');
                }
            };
    }

    if (map.add) {
        if (!addType || !addAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).add = (grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof addType, any, any> =>
            async(dispatch) => {
                dispatch({
                    type: addType,
                    payload: { grandGrandParentID, grandParentID, parentID, entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entity.ID;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(addAction(grandGrandParentID, grandParentID, parentID, entity));
                }
                else {
                    if (result.load) {
                        const newID = await dispatch(addAction(grandGrandParentID, grandParentID, parentID, entity));
                        return await dispatch(result.load(grandGrandParentID, grandParentID, parentID, newID!, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }

    if (map.edit) {
        const
            type = map.edit.type!,
            method = map.edit.method!,

            editAction = (grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>) => networkAction(
                type, { grandGrandParentID, grandParentID, parentID, entityID: entity.ID, entity }, method, entity
            );

        (result as any).edit = (grandGrandParentID: GG['ID'], grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { grandGrandParentID, grandParentID, parentID, entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                const syncAction =
                    !entity.Custom || entity.Custom.status === RequestStatus.UNSYNCED ?
                        addAction as unknown as typeof editAction :
                        editAction;

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(syncAction(grandGrandParentID, grandParentID, parentID, entity));
                }
                else {
                    if (result.load) {
                        await dispatch(syncAction(grandGrandParentID, grandParentID, parentID, entity));
                        return await dispatch(result.load(grandGrandParentID, grandParentID, parentID, entity.ID, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }


    if (map.deleteAll) {
        const
            type = map.deleteAll.type!,
            method = map.deleteAll.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.deleteAll = (grandGrandParentID, grandParentID, parentID, entities) => async(dispatch) => {
            const
                localEntityIDs = entities.filter(({ Custom: { status } }) => status === RequestStatus.UNSYNCED).map(({ ID }) => ID),
                remoteEntityIDs = entities.filter(({ Custom: { status } }) => status !== RequestStatus.UNSYNCED).map(({ ID }) => ID);

            if (localEntityIDs.length) {
                dispatch({
                    type,
                    payload: { status: RequestStatus.SUCCEEDED, grandGrandParentID, grandParentID, parentID, entityIDs: localEntityIDs, response: undefined }
                });
            }
            if (remoteEntityIDs.length) {
                await dispatch(networkAction(type, { grandGrandParentID, grandParentID, parentID, entityIDs: remoteEntityIDs }, method, remoteEntityIDs));
            }
        };
    }

    if (map.delete) {
        const
            type = map.delete.type!,
            method = map.delete.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.delete = (grandGrandParentID, grandParentID, parentID, entity, sync = true) => async(dispatch) => {
            if (!sync || entity.Custom.status === RequestStatus.UNSYNCED) {
                dispatch({
                    type,
                    payload: { status: RequestStatus.SUCCEEDED, grandGrandParentID, grandParentID, parentID, entityID: entity.ID, response: undefined }
                });
            }
            else {
                await dispatch(networkAction(type, { grandGrandParentID, grandParentID, parentID, entityID: entity.ID }, method, entity.ID));
            }
        };
    }

    if (map.voidAll) {
        const { type } = map.voidAll;
        result.voidAll = (grandGrandParentID, grandParentID, parentID, immediately = true) => ({
            type,
            payload: { grandGrandParentID, grandParentID, parentID, immediately }
        });
    }

    if (map.void) {
        const { type } = map.void;
        result.void = (grandGrandParentID, grandParentID, parentID, entityID, immediately = true) => ({
            type,
            payload: { grandGrandParentID, grandParentID, parentID, entityID, immediately }
        });
    }

    return result as unknown as TripleNestedEntityMappedActionCreators<GG, G, P, E, M>;
}


